import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { customerService, washService, subscriptionService } from '../services/firebase'
import {
  Users,
  Car,
  DollarSign,
  Clock,
  BarChart3,
  Activity,
  Star,
  Award,
  CheckCircle,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
function washDate(wash) {
  if (!wash?.createdAt) return 'N/A'
  try {
    const d = wash.createdAt.toDate ? wash.createdAt.toDate() : new Date(wash.createdAt)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return 'N/A'
  }
}

function statusBadgeVariant(status) {
  const s = (status || '').toLowerCase()
  if (['completed', 'ready'].includes(s)) return 'default'
  if (['waiting', 'pending'].includes(s)) return 'secondary'
  if (['washing', 'drying'].includes(s)) return 'outline'
  return 'outline'
}

export default function DashboardPage() {
  const { userProfile, currentUser } = useAuth()
  const { currentBranch } = useBranch()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalWashes: 0,
    totalRevenue: 0,
    averageRating: 0,
    activeWashes: 0,
    completedToday: 0,
    pendingWashes: 0,
    totalSubscriptions: 0,
  })
  const [washes, setWashes] = useState([])

  useEffect(() => {
    loadDashboardData()
  }, [currentBranch])

  const loadDashboardData = async () => {
    if (!currentBranch) return

    setLoading(true)
    try {
      const [customers, washesData, subscriptions] = await Promise.all([
        customerService.getAllCustomers(),
        washService.getWashesByBranch(currentBranch.id),
        subscriptionService.getSubscriptionsByBranch(currentBranch.id),
      ])

      setWashes(washesData)

      const totalCustomers = customers.length
      const totalWashes = washesData.length
      const totalRevenue = washesData.reduce((sum, wash) => sum + (wash.amount || 0), 0)
      const averageRating =
        washesData.length > 0
          ? washesData.reduce((sum, wash) => sum + (wash.rating || 0), 0) / washesData.length
          : 0
      const activeWashes = washesData.filter((w) =>
        ['waiting', 'washing', 'drying'].includes(w.status)
      ).length
      const completedToday = washesData.filter((w) => {
        if (w.status !== 'completed') return false
        const d = w.createdAt?.toDate ? w.createdAt.toDate() : new Date(w.createdAt)
        return d.toDateString() === new Date().toDateString()
      }).length
      const pendingWashes = washesData.filter((w) => w.status === 'waiting').length
      const totalSubscriptions = subscriptions.filter((s) => s.isActive).length

      setStats({
        totalCustomers,
        totalWashes,
        totalRevenue,
        averageRating,
        activeWashes,
        completedToday,
        pendingWashes,
        totalSubscriptions,
      })
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      setStats({
        totalCustomers: 0,
        totalWashes: 0,
        totalRevenue: 0,
        averageRating: 0,
        activeWashes: 0,
        completedToday: 0,
        pendingWashes: 0,
        totalSubscriptions: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  const isViewingAllBranches = () => !currentBranch || currentBranch.id === 'all-branches'

  const statCards = [
    {
      name: 'Customers',
      value: stats.totalCustomers.toLocaleString(),
      sub: 'registered',
      icon: Users,
    },
    {
      name: 'Washes',
      value: stats.totalWashes.toLocaleString(),
      sub: 'all time',
      icon: Car,
    },
    {
      name: 'Revenue',
      value: `R${stats.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      sub: 'branch total',
      icon: DollarSign,
    },
    {
      name: 'Avg. rating',
      value: stats.averageRating.toFixed(1),
      sub: 'from feedback',
      icon: Star,
    },
  ]

  const quickStats = [
    { name: 'In progress', value: stats.activeWashes, icon: Activity },
    { name: 'Done today', value: stats.completedToday, icon: CheckCircle },
    { name: 'In queue', value: stats.pendingWashes, icon: Clock },
    { name: 'Subscriptions', value: stats.totalSubscriptions, icon: Award },
  ]

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="space-y-2">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-8">
      <div className="flex flex-col gap-1 border-b border-border pb-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <BarChart3 className="size-5 shrink-0" />
          <span className="text-xs font-medium uppercase tracking-wider">Overview</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          {currentUser?.displayName || userProfile?.displayName || userProfile?.name || 'Dashboard'}
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          {isViewingAllBranches()
            ? 'Performance across all branches.'
            : `${currentBranch?.name || 'Branch'} - key metrics and latest washes.`}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card
            key={stat.name}
            className="overflow-hidden border-border/80 shadow-sm transition-shadow hover:shadow-md"
          >
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.name}
              </CardTitle>
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <stat.icon className="size-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums tracking-tight">
                {stat.value}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{stat.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {quickStats.map((stat) => (
          <Card key={stat.name} className="border-dashed bg-muted/20 shadow-none">
            <CardContent className="flex items-center gap-3 pt-6">
              <div className="rounded-lg border border-border bg-background p-2 text-muted-foreground">
                <stat.icon className="size-4" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums leading-none">{stat.value}</p>
                <p className="text-xs font-medium text-muted-foreground mt-1">{stat.name}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Recent washes</CardTitle>
          <CardDescription>Latest activity for this branch</CardDescription>
        </CardHeader>
        <CardContent className="space-y-0">
          {washes.slice(0, 8).map((wash, idx) => (
            <div key={wash.id}>
              {idx > 0 && <Separator className="my-0" />}
              <div className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
                    <Car className="size-4 shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {wash.customerName || 'Walk-in'}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {wash.serviceType || 'Wash'} · R
                      {(wash.amount ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:items-end sm:gap-1">
                  <Badge variant={statusBadgeVariant(wash.status)} className="capitalize">
                    {wash.status || 'N/A'}
                  </Badge>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {washDate(wash)}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {washes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="rounded-full bg-muted p-4 text-muted-foreground mb-3">
                <Car className="size-8" />
              </div>
              <p className="text-sm font-medium text-foreground">No wash activity yet</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                Completed and queued washes will show here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
